<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="700px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    {{ dialogTitle }}
                </h1>
            </v-card-title>

            <v-card-text>
                <v-row class="pt-1">
                    <v-col cols="12" md="6" class="py-0">
                        <v-select
                            v-model="channel"
                            label="Kanał"
                            :items="channels"
                            :error-messages="channelErrors"
                            item-text="name"
                            return-object
                            no-data-text="Nie znaleziono kanału"
                            dense
                            outlined
                            clearable
                            @change="channelChanged"
                        />
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <v-autocomplete
                            v-if="!machineDigitalChannel && channel"
                            v-model="objectNumber"
                            label="Numer zlecenia"
                            :items="objectNumbers"
                            :loading="loading"
                            :search-input.sync="objectNumberSearchQuery"
                            :error-messages="objectNumberErrors"
                            class="v-text-field--placeholder-dark"
                            item-text="number"
                            return-object
                            dense
                            outlined
                            required
                            no-data-text="Nie znaleziono zlecenia"
                            @change="changeNumber"
                        >
                            <template slot="no-data">
                                <v-list-item>
                                    <v-list-item-title>
                                        <template v-if="!objectNumberSearchQuery">
                                            Wpisz treść aby odnaleźć <strong>numer zlecenia</strong>.
                                        </template>
                                        <template v-else-if="objectNumberSearchQuery.length < objectNumberSearchQueryMinLength">
                                            Wpisz przynajmniej <strong>{{ objectNumberSearchQueryMinLength }}</strong> znaki.
                                        </template>
                                        <template v-else-if="loading">
                                            Trwa wyszukiwanie numera zlecenia...
                                        </template>
                                        <template v-else>
                                            Nie odnaleziono numer zlecenia na zapytanie: "<strong> {{ objectNumberSearchQuery }}".</strong>
                                        </template>
                                    </v-list-item-title>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6" class="py-0">
                        <v-select
                            v-model="paper"
                            label="Rodzaj surowca"
                            :items="papers"
                            :error-messages="paperErrors"
                            return-object
                            item-text="name"
                            no-data-text="Nie znaleziono papieru"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="partsCount"
                            label="Ilość użytków po krojeniu"
                            :error-messages="partsCountErrors"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="inputFormatA"
                            :error-messages="inputFormatAErrors"
                            label="Format A na wejściu"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="inputFormatB"
                            :error-messages="inputFormatBErrors"
                            label="Format B na wejściu"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="outputFormatA"
                            :error-messages="outputFormatAErrors"
                            label="Format A na wyjściu"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="outputFormatB"
                            :error-messages="outputFormatBErrors"
                            label="Format B na wyjściu"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="inputSheetsCount"
                            :error-messages="inputSheetsCountErrors"
                            label="Ilość arkuszy na wejściu"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="weightInput"
                            :error-messages="weightInputErrors"
                            label="Waga na wejściu"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" class="py-0">
                        <v-text-field
                            v-model="rawMaterialName"
                            label="Nazwa surowa"
                            maxlength="128"
                            counter="128"
                            dense
                            outlined
                            clearable
                        />
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    @click="saveCuttingJob"
                >
                    {{ actionButtonLabel }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import { errorHandler } from '@/utils/errorHandler'
import { decimal, numeric, requiredIf, minValue, integer, required } from 'vuelidate/lib/validators'

const CHANNEL = {
    NCF: 'NCF',
    ONLINE: 'ONLINE',
    RW_RS: 'RW/RS',
    MASZYNY_CYFROWE: 'MASZYNY CYFROWE'
}

export default {
    name: 'SaveCuttingJobDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        channels: {
            type: Array,
            default: () => []
        },
        papers: {
            type: Array,
            default: () => []
        },
        cuttingJob: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            channel: null,
            objectNumber: null,
            objectNumbers: [],
            paper: null,
            partsCount: 1,
            inputFormatA: null,
            inputFormatB: null,
            outputFormatA: null,
            outputFormatB: null,
            inputSheetsCount: null,
            weightInput: null,
            rawMaterialName: null,
            loading: false,
            objectNumberSearchQuery: null,
            objectNumberSearchQueryMinLength: 3,
        }
    },
    computed: {
        isEditMode() {
            return Boolean(this.cuttingJob)
        },
        dialogTitle() {
            return this.isEditMode
                ? 'Edycja zlecenia docięcia'
                : 'Tworzenie nowego zlecenia'
        },
        actionButtonLabel() {
            return this.isEditMode ? 'Zapisz' : 'Utwórz'
        },
        machineDigitalChannel() {
            return this.channel?.name === CHANNEL.MASZYNY_CYFROWE
        },
        objectNumbersSourcePath() {
            return {
                [CHANNEL.NCF]: 'jobs/getJobNumbers',
                [CHANNEL.ONLINE]: 'cuttingJobs/getCuttingJobReportItems',
                [CHANNEL.RW_RS]: 'cuttingJobs/getCuttingJobReportItems',
            }[this.channel?.name] || null
        },
        queryFilter() {
            if (this.channel.name === CHANNEL.ONLINE) {
                return `?type_ids=10,20,80&search=${this.objectNumberSearchQuery}`
            }

            if (this.channel.name === CHANNEL.RW_RS) {
                return `?type_ids=10,20,70,80&search=${this.objectNumberSearchQuery}`
            }

            return `?search=${this.objectNumberSearchQuery}`
        },

        // Validation errors
        channelErrors() {
            const errors = []

            if (!this.$v.channel.$dirty) return errors
            !this.$v.channel.required && errors.push('Kanał jest wymagany.')

            return errors
        },
        objectNumberErrors() {
            const errors = []

            if (!this.$v.objectNumber.$dirty) return errors
            !this.$v.objectNumber.required && errors.push('Numer zlecenia jest wymagany.')

            return errors
        },
        paperErrors() {
            const errors = []

            if (!this.$v.paper.$dirty) return errors
            !this.$v.paper.required && errors.push('Rodzaj surowca jest wymagany.')

            return errors
        },
        partsCountErrors() {
            const errors = []

            if (!this.$v.partsCount.$dirty) return errors
            !this.$v.partsCount.required && errors.push('Ilość użytków jest wymagana.')
            !this.$v.partsCount.minValue && errors.push('Wartość musi być większa niż 0.')
            !this.$v.partsCount.integer && errors.push('Wartość musi być liczbą całkowitą.')

            return errors
        },
        inputFormatAErrors() {
            const errors = []

            if (!this.$v.inputFormatA.$dirty) return errors
            !this.$v.inputFormatA.required && errors.push('Szerokość jest wymagana.')
            !this.$v.inputFormatA.minValue && errors.push('Wartość musi być większa niż 0.')
            !this.$v.inputFormatA.integer && errors.push('Wartość musi być liczbą całkowitą.')

            return errors
        },
        inputFormatBErrors() {
            const errors = []

            if (!this.$v.inputFormatB.$dirty) return errors
            !this.$v.inputFormatB.required && errors.push('Szerokość jest wymagana.')
            !this.$v.inputFormatB.minValue && errors.push('Wartość musi być większa niż 0.')
            !this.$v.inputFormatB.integer && errors.push('Wartość musi być liczbą całkowitą.')

            return errors
        },
        outputFormatAErrors() {
            const errors = []

            if (!this.$v.outputFormatA.$dirty) return errors
            !this.$v.outputFormatA.required && errors.push('Szerokość jest wymagana.')
            !this.$v.outputFormatA.minValue && errors.push('Wartość musi być większa niż 0.')
            !this.$v.outputFormatA.integer && errors.push('Wartość musi być liczbą całkowitą.')

            return errors
        },
        outputFormatBErrors() {
            const errors = []

            if (!this.$v.outputFormatB.$dirty) return errors
            !this.$v.outputFormatB.required && errors.push('Szerokość jest wymagana.')
            !this.$v.outputFormatB.minValue && errors.push('Wartość musi być większa niż 0.')
            !this.$v.outputFormatB.integer && errors.push('Wartość musi być liczbą całkowitą.')

            return errors
        },
        inputSheetsCountErrors() {
            const errors = []

            if (!this.$v.inputSheetsCount.$dirty) return errors

            !this.$v.inputSheetsCount.numeric && errors.push('Wartość musi być liczbą.')
            !this.$v.inputSheetsCount.required && this.$v.inputSheetsCount.$invalid &&
                errors.push('Wartość jest wymagana, jeśli nie podano wagi.')
            this.$v.inputSheetsCount.$model && this.$v.weightInput.$model &&
                errors.push('Nie można podać zarówno ilości arkuszy, jak i wagi.')

            return errors
        },
        weightInputErrors() {
            const errors = []

            if (!this.$v.weightInput.$dirty) return errors
            !this.$v.weightInput.decimal && errors.push('Wartość musi być liczbą.')
            !this.$v.weightInput.required && this.$v.weightInput.$invalid &&
                errors.push('Wartość jest wymagana, jeśli nie podano ilości arkuszy.')
            this.$v.weightInput.$model && this.$v.inputSheetsCount.$model &&
                errors.push('Nie można podać zarówno ilości arkuszy, jak i wagi.')

            return errors
        }
    },
    watch: {
        objectNumberSearchQuery(value = null) {
            if (value?.length < this.objectNumberSearchQueryMinLength || value === this.objectNumber) {
                return
            }

            value
                ? this.searchItems()
                : this.objectNumbers = []
        },
        cuttingJob: {
            handler() {
                if (this.isEditMode && this.isOpen) {
                    this.populateForm()
                }
            },
            deep: true
        },
        isOpen(value) {
            if (value && this.isEditMode) {
                this.populateForm()
            }
        }
    },
    validations() {
        let rules = {
            channel: {
                required
            },
            paper: {
                required
            },
            partsCount: {
                required,
                integer,
                minValue: minValue(1)
            },
            inputFormatA: {
                required,
                integer,
                minValue: minValue(1)
            },
            inputFormatB: {
                required,
                integer,
                minValue: minValue(1)
            },
            outputFormatA: {
                required,
                integer,
                minValue: minValue(1)
            },
            outputFormatB: {
                required,
                integer,
                minValue: minValue(1)
            },
            inputSheetsCount: {
                numeric,
                required: requiredIf(()=> !this.weightInput),
            },
            weightInput: {
                decimal,
                required: requiredIf(() => !this.inputSheetsCount),
            }
        }

        if (!this.machineDigitalChannel || !this.channel) {
            rules.objectNumber = {
                required
            }
        }

        return rules
    },
    methods: {
        populateForm() {
            const cuttingJob = this.cuttingJob

            if (!cuttingJob) {
                return
            }

            const paperId = cuttingJob.paper?.id
            const channelId = cuttingJob.channel?.id

            this.channel = this.channels.find(channel => channel.id === channelId) || cuttingJob.channel || null
            this.paper = this.papers.find(paper => paper.id === paperId) || cuttingJob.paper || null
            this.objectNumber = cuttingJob.objectNumber || null
            this.objectNumberSearchQuery = cuttingJob.objectNumber || null
            this.partsCount = cuttingJob.partsCount?.toString() || 1
            this.inputFormatA = cuttingJob.inputFormatA?.toString() || null
            this.inputFormatB = cuttingJob.inputFormatB?.toString() || null
            this.outputFormatA = cuttingJob.outputFormatA?.toString() || null
            this.outputFormatB = cuttingJob.outputFormatB?.toString() || null
            this.inputSheetsCount = cuttingJob.inputSheetsCount?.toString() || null
            this.weightInput = cuttingJob.inputSheetsWeight?.toString() || null
            this.rawMaterialName = cuttingJob.rawMaterialName || null
        },
        changeNumber(number) {
            this.objectNumber = number.number
        },
        searchItems() {
            this.$store.dispatch(this.objectNumbersSourcePath, {
                query: this.queryFilter
            })
                .then(({data}) => {
                    this.objectNumbers = (data.results || []).map(item => {
                        const numberValue = item.primaryNumber || item.number
                            
                        return {
                            ...item,
                            number: numberValue,
                            primaryNumber: numberValue
                        }
                    })
                })
                .catch((error) => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        channelChanged() {
            this.objectNumber = null
            this.objectNumbers = []
        },
        async saveCuttingJob() {
            this.$v.$touch()

            if (this.$v.$invalid || (this.$v.inputSheetsCount.$model && this.$v.weightInput.$model)) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            if (this.isEditMode) {
                try {
                    await this.$store.dispatch('cuttingJobs/canEditCuttingJob', {
                        id: this.cuttingJob.id
                    })
                } catch {
                    return
                }
            }

            const payload = {
                channelId: this.channel.id,
                paperId: this.paper.id,
                partsCount: +this.partsCount,
                inputFormatA: +this.inputFormatA,
                inputFormatB: +this.inputFormatB,
                outputFormatA: +this.outputFormatA,
                outputFormatB: +this.outputFormatB,
                rawMaterialName: this.rawMaterialName,
            }

            if (this.objectNumber) {
                payload.objectNumber = this.objectNumber
            }

            if (this.rawMaterialName === '') {
                payload.rawMaterialName = null
            }

            if (this.inputSheetsCount) {
                payload.inputSheetsCount = +this.inputSheetsCount
            } else if (this.weightInput) {
                payload.inputSheetsWeight = +this.weightInput
            }

            const action = this.isEditMode
                ? this.$store.dispatch('cuttingJobs/updateCuttingJob', {
                    id: this.cuttingJob.id,
                    payload
                })
                : this.$store.dispatch('cuttingJobs/createCuttingJob', payload)

            action
                .then(() => {
                    this.onSuccess()
                })
                .catch((error) => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        onSuccess() {
            this.$store.dispatch('showNotification', {
                type: 'success',
                message: this.isEditMode
                    ? 'Zlecenie zostało zaktualizowane pomyślnie.'
                    : 'Zlecenie zostało utworzone pomyślnie.'
            })
            this.$emit('on-success')
            this.onClose()
        },
        onClose() {
            this.$emit('on-close')
            this.resetForm()
        },
        resetForm() {
            setTimeout(() => {
                this.channel = null
                this.objectNumber = null
                this.objectNumbers = []
                this.paper = null
                this.partsCount = 1
                this.inputFormatA = null
                this.inputFormatB = null
                this.outputFormatA = null
                this.outputFormatB = null
                this.inputSheetsCount = null
                this.weightInput = null
                this.rawMaterialName = null
                this.loading = false
                this.objectNumberSearchQuery = null
                this.$v.$reset()
            }, 500)
        },
    }
}
</script>
